import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { useTheme } from '@material-ui/core/styles';
import { isWidthUp } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Badge from '@material-ui/core/Badge';
import FormControl from '@material-ui/core/FormControl';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';

import Skeleton from '@material-ui/lab/Skeleton';

import { DIMENSIONS } from '../../styles/constants';
import { CHECKBOX_FILTERS } from './constants';
import { areRangeFiltersUsed, calcNumberOfUsedFilters, filterItems } from './utils';
import { useStyles, useBadgeStyles } from './styles';
import designListSlice from '../../redux/slices/designListSlice';
import filterbarSlice from './filterbarSlice';
import RangeFilters from './RangeFilters';
import AllFiltersDialog from './AllFiltersDialog';
import FilterSelectButton from './FilterSelect/FilterSelectButton';
import FilterSelect from './FilterSelect';
import Button from '../Button';
import { useTranslation } from 'react-i18next';

const Filterbar = ({ searchValue, setSearchValue, screenWidth }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const badgeClasses = useBadgeStyles();
	const theme = useTheme();
	const [numberOfUsedFilters, setNumberOfUsedFilters] = useState(0);
	const [openedFilterSelects, setOpenedFilterSelects] = useState([]);
	const filterGroups = useSelector(state => state.filterbar.filterGroups);
	const checkedFilters = useSelector(state => state.filterbar.checkedFilters);
	const items = useSelector(state => state.designList.items);
	const rangeFiltersMinMax = useSelector(state => state.designList.rangeFiltersMinMax);
	const rangeFilterValues = useSelector(state => state.filterbar.rangeFilterValues);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const dispatch = useDispatch();

	const isWidthUpSm = isWidthUp('sm', screenWidth);

	useEffect(() => {
		dispatch(designListSlice.actions.filterItems(filterItems(items, checkedFilters, rangeFilterValues, searchValue)));
		setNumberOfUsedFilters(calcNumberOfUsedFilters(checkedFilters, rangeFiltersMinMax, rangeFilterValues, searchValue));
	}, [dispatch, checkedFilters, items, rangeFilterValues, searchValue, rangeFiltersMinMax]);

	const handleFilterSelectOpen = selectId => {
		const currentIndex = openedFilterSelects.indexOf(selectId);
		const newOpened = [...openedFilterSelects];

		if (currentIndex === -1) {
			newOpened.push(selectId);
		} else {
			newOpened.splice(currentIndex, 1);
		}

		setOpenedFilterSelects(newOpened);
	};

	const handleFilterCheck = useMemo(
		() => (category, checkedFilterNames) => {
			dispatch(
				filterbarSlice.actions.setCheckedFilters({
					type: category,
					filters: checkedFilterNames
				})
			);
		},
		[dispatch]
	);

	const clearFilters = () => {
		setSearchValue('');
		dispatch(filterbarSlice.actions.clearFilters(rangeFiltersMinMax));
	};

	return (
		<Grid container alignItems="flex-end" spacing={2}>
			{isWidthUpSm &&
				(progressActive ? (
					[...Array(6)].map((_e, index) => (
						<Grid item key={'filter-button-skeleton-' + index}>
							<Skeleton width={DIMENSIONS.SKELETON_BUTTON_WIDTH} height={DIMENSIONS.SKELETON_BUTTON_HEIGHT} />
						</Grid>
					))
				) : items.length < 1 ? (
					<Fragment>
						<Grid item>
							<FilterSelectButton disabled>{t('components.filterBar.designAttributes')}</FilterSelectButton>
						</Grid>

						{CHECKBOX_FILTERS.map(cbFilter => (
							<Grid item key={cbFilter.label}>
								<FilterSelectButton disabled>{cbFilter.label}</FilterSelectButton>
							</Grid>
						))}
					</Fragment>
				) : (
					<Fragment>
						{Object.keys(rangeFiltersMinMax).length > 0 && rangeFiltersMinMax.constructor === Object && (
							<Grid item>
								<FormControl>
									<Popover
										open={openedFilterSelects.indexOf('designAttributes') !== -1}
										anchorEl={document.getElementById('Filters__designAttributes_select_button')}
										onClose={() => handleFilterSelectOpen('designAttributes')}
										getContentAnchorEl={null}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'left'
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'left'
										}}
										PaperProps={{
											style: {
												boxShadow: theme.shadows[2]
											}
										}}>
										<RangeFilters />
									</Popover>

									<FilterSelectButton
										active={areRangeFiltersUsed(rangeFiltersMinMax, rangeFilterValues)}
										id={'Filters__designAttributes_select_button'}
										htmlFor={'design-attributes-select'}
										onClick={() => handleFilterSelectOpen('designAttributes')}>
										{t('components.filterBar.designAttributes')}
									</FilterSelectButton>
								</FormControl>
							</Grid>
						)}

						{Object.entries(filterGroups).map(([category, filters]) => (
							<Grid item key={category + '-filter-form'}>
								<FilterSelect
									category={category}
									filters={filters}
									openedFilterSelects={openedFilterSelects}
									handleFilterSelectOpen={handleFilterSelectOpen}
									handleFilterCheck={handleFilterCheck}
								/>
							</Grid>
						))}
					</Fragment>
				))}

			{progressActive ? (
				<Grid item>
					<Skeleton width={DIMENSIONS.SKELETON_BUTTON_WIDTH} height={DIMENSIONS.SKELETON_BUTTON_HEIGHT} />
				</Grid>
			) : (
				<AllFiltersDialog screenWidth={screenWidth} disabled={items.length < 1} handleFilterCheck={handleFilterCheck} />
			)}

			{numberOfUsedFilters > 0 && (
				<Grid item>
					<Button size="small" onClick={() => clearFilters()} className={classes.resetButton}>
						<Badge badgeContent={numberOfUsedFilters} classes={badgeClasses}>
							<Typography>{t('components.filterBar.buttons.reset')}</Typography>
						</Badge>
					</Button>
				</Grid>
			)}
		</Grid>
	);
};

Filterbar.propTypes = {
	searchValue: PropTypes.string.isRequired,
	setSearchValue: PropTypes.func.isRequired,
	screenWidth: PropTypes.number.isRequired
};

export default Filterbar;
